import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  User2Icon, MenuIcon, X, ChevronDown, 
  LogOut, Bell, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext'; // Importamos useAuth directamente

// Import components
import PageHeader from './staff/PageHeader';
import MobileMenu from './staff/MobileMenu';
import ReportsTab from './staff/tabs/ReportsTab';
import UsersTab from './staff/tabs/UsersTab';
import PropertiesTab from './staff/tabs/PropertiesTab';
import ActionHistoryTab from './staff/tabs/ActionHistoryTab';
import ContactTab from './staff/tabs/ContactTab';
import RegisterConstructoraTab from './staff/tabs/RegisterConstructoraTab';
import NotificationToast from './staff/NotificationToast';
import LoadingIndicator from './staff/LoadingIndicator';

// Import dialog components
import PropertyDialog from './staff/dialogs/PropertyDialog';
import DeletePropertyDialog from './staff/dialogs/DeletePropertyDialog';
import ContactDialog from './staff/dialogs/ContactDialog';

// Set axios defaults
axios.defaults.baseURL = 'https://backend-production-1786.up.railway.app';

// Main admin dashboard component for staff
const StaffDashboard = () => {
  // State for controlling active tab
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // Cambiado a false inicial
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [currentUser, setCurrentUser] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const navigate = useNavigate();
  
  // CLAVE: Usar tanto user como loading del AuthContext, y también logout
  const { user, loading: authLoading, logout } = useAuth();

  // Shared state for property dialog
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editPropertyData, setEditPropertyData] = useState({});
  const [deletePropertyDialogOpen, setDeletePropertyDialogOpen] = useState(false);
  const [propertyOwner, setPropertyOwner] = useState(null);

  // Verificar tipo de usuario usando el contexto de autenticación
  useEffect(() => {
    // IMPORTANTE: No hacer nada mientras AuthContext está cargando
    if (authLoading) {
      console.log('AuthContext está cargando...');
      return;
    }

    console.log('Usuario desde AuthContext:', user);
    
    // Solo después de que AuthContext termine de cargar, verificar usuario
    if (!user) {
      console.log('No hay usuario autenticado, redirigiendo a login');
      showNotification('Debes iniciar sesión para acceder', 'error');
      navigate('/login');
      return;
    }
    
    // Verificar que el usuario sea staff (tipo 5)
    if (user.tipo_usuario_id !== 5) {
      console.error('Usuario no autorizado:', user.tipo_usuario_id);
      showNotification('No tienes permisos para acceder a esta sección', 'error');
      navigate('/admin/dashboard'); // Corregido: era '/admln/dashboard'
      return;
    }
    
    // Si es tipo 5, permitir acceso y establecer como usuario actual
    console.log('Usuario autorizado como staff:', user);
    setCurrentUser(user);
    
  }, [user, authLoading, navigate]); // Agregado authLoading a las dependencias

  // Function to show notifications
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };
  
  // Function to close notifications
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Handle logout
  const handleLogout = () => {
    // Log logout action
    if (currentUser?.id) {
      logAction('USUARIO_LOGOUT', 'Cierre de sesión', currentUser.id, 'user');
    }
    
    // Use AuthContext logout method
    logout();
    
    // Redirect to login
    navigate('/login');
  };
  
  // Function to log actions
  const logAction = async (action, details, targetId = null, targetType = null) => {
    try {
      if (currentUser?.id) {
        await axios.post('/api/admin/log-action', {
          staff_id: currentUser.id,
          action,
          details,
          target_id: targetId,
          target_type: targetType
        });
      }
    } catch (error) {
      console.error('Error al registrar acción:', error);
    }
  };

  // Function to open the property dialog from any component
  const handleViewProperty = async (property) => {
    setSelectedProperty(property);
    setEditPropertyData({ ...property });
    
    // Get owner information if we have an id_vendedor
    if (property.id_vendedor) {
      try {
        const response = await axios.get(`/api/admin/users/${property.id_vendedor}`);
        setPropertyOwner(response.data);
      } catch (error) {
        console.error('Error al obtener datos del propietario:', error);
        setPropertyOwner(null);
      }
    } else {
      setPropertyOwner(null);
    }
    
    setPropertyDialogOpen(true);
    
    // Log action
    if (currentUser?.id) {
      logAction(
        'PROPIEDAD_VISTA',
        `Se ha visto la propiedad ${property.id} - ${property.titulo || 'Sin título'}`,
        property.id,
        'property'
      );
    }
  };
  
  // Save property changes
  const handleSaveProperty = async () => {
    setIsLoading(true);
    try {
      await axios.put(`/api/admin/properties/${selectedProperty.id}`, editPropertyData);
      
      showNotification('Propiedad actualizada correctamente', 'success');
      if (currentUser?.id) {
        logAction(
          'PROPIEDAD_ACTUALIZADA',
          `Se ha actualizado la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
          selectedProperty.id,
          'property'
        );
      }
      
      setPropertyDialogOpen(false);
    } catch (error) {
      console.error('Error al actualizar propiedad:', error);
      showNotification(`Error al actualizar propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Suspend property
  const handleSuspendProperty = async () => {
    setIsLoading(true);
    try {
      await axios.put(`/api/admin/properties/${selectedProperty.id}/suspend`);
      
      showNotification('Propiedad suspendida correctamente', 'success');
      if (currentUser?.id) {
        logAction(
          'PROPIEDAD_SUSPENDIDA',
          `Se ha suspendido la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
          selectedProperty.id,
          'property'
        );
      }
      
      setPropertyDialogOpen(false);
    } catch (error) {
      console.error('Error al suspender propiedad:', error);
      showNotification(`Error al suspender propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete property
  const handleDeleteProperty = async () => {
    setIsLoading(true);
    try {
      await axios.delete(`/api/admin/properties/${selectedProperty.id}`);
      
      showNotification('Propiedad eliminada correctamente', 'success');
      if (currentUser?.id) {
        logAction(
          'PROPIEDAD_ELIMINADA',
          `Se ha eliminado la propiedad ${selectedProperty.titulo} (${selectedProperty.id})`,
          selectedProperty.id,
          'property'
        );
      }
      
      setDeletePropertyDialogOpen(false);
      setPropertyDialogOpen(false);
    } catch (error) {
      console.error('Error al eliminar propiedad:', error);
      showNotification(`Error al eliminar propiedad: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // User navigation function (to switch to users tab when clicking on a user)
  const handleViewUser = (user) => {
    // Switch to users tab
    setTabValue(1); // Users tab is at index 1
    
    // You can add code here to search for the user in the UsersTab
    // For example, set a state that UsersTab uses to know which user to search for
    
    showNotification(`Navegando a los detalles del usuario: ${user.nombre}`, 'info');
  };

  // Mostrar loading mientras AuthContext está cargando o mientras no hay usuario válido
  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {authLoading ? 'Verificando autenticación...' : 'Cargando...'}
          </p>
        </div>
      </div>
    );
  }

  // Si el usuario no es tipo 5 después de la carga, no mostrar nada (el useEffect se encargará del redirect)
  if (user && user.tipo_usuario_id !== 5) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-gray-600">Verificando permisos...</p>
        </div>
      </div>
    );
  }

  // Main structure of the component - Se muestra solo si el usuario es staff (tipo 5)
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
      {/* Header with navigation */}
      <PageHeader 
        currentUser={currentUser}
        tabValue={tabValue}
        setTabValue={setTabValue}
        userDropdownOpen={userDropdownOpen}
        setUserDropdownOpen={setUserDropdownOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        handleLogout={handleLogout}
      />
      
      {/* Mobile menu */}
      <MobileMenu 
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        tabValue={tabValue}
        setTabValue={setTabValue}
        currentUser={currentUser}
        handleLogout={handleLogout}
      />
      
      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <AnimatePresence mode="wait">
          {/* TAB 1: REPORTS */}
          {tabValue === 0 && (
            <motion.div
              key="reports-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <ReportsTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
                logAction={logAction}
                handleViewProperty={handleViewProperty}
                handleViewUser={handleViewUser}
              />
            </motion.div>
          )}
          
          {/* TAB 2: USERS */}
          {tabValue === 1 && (
            <motion.div
              key="users-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <UsersTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
                logAction={logAction}
                handleViewProperty={handleViewProperty}
              />
            </motion.div>
          )}
          
          {/* TAB 3: PROPERTIES */}
          {tabValue === 2 && (
            <motion.div
              key="properties-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <PropertiesTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
                logAction={logAction}
                handleViewUser={handleViewUser}
                handleViewProperty={handleViewProperty}
              />
            </motion.div>
          )}
          
          {/* TAB 4: ACTION HISTORY */}
          {tabValue === 3 && (
            <motion.div
              key="history-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <ActionHistoryTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
              />
            </motion.div>
          )}
          
          {/* TAB 5: CONTACTS */}
          {tabValue === 4 && (
            <motion.div
              key="contacts-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <ContactTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
                logAction={logAction}
              />
            </motion.div>
          )}
          
          {/* TAB 6: REGISTER CONSTRUCTORA */}
          {tabValue === 5 && (
            <motion.div
              key="register-constructora-tab"
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, transition: { duration: 0.2 } }}
              variants={{
                hidden: { opacity: 0 },
                visible: { 
                  opacity: 1,
                  transition: { 
                    staggerChildren: 0.1
                  } 
                }
              }}
            >
              <RegisterConstructoraTab 
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                currentUser={currentUser}
                showNotification={showNotification}
                logAction={logAction}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      
      {/* Shared Dialogs */}
      <PropertyDialog
        propertyDialogOpen={propertyDialogOpen}
        setPropertyDialogOpen={setPropertyDialogOpen}
        selectedProperty={selectedProperty}
        editPropertyData={editPropertyData}
        setEditPropertyData={setEditPropertyData}
        handleSaveProperty={handleSaveProperty}
        handleSuspendProperty={handleSuspendProperty}
        setDeletePropertyDialogOpen={setDeletePropertyDialogOpen}
        propertyOwner={propertyOwner}
        handleViewUser={handleViewUser}
      />
      
      <DeletePropertyDialog 
        deletePropertyDialogOpen={deletePropertyDialogOpen}
        setDeletePropertyDialogOpen={setDeletePropertyDialogOpen}
        selectedProperty={selectedProperty}
        handleDeleteProperty={handleDeleteProperty}
      />
      
      {/* Notifications */}
      <NotificationToast 
        notification={notification}
        handleCloseNotification={handleCloseNotification}
      />
      
      {/* Loading indicator */}
      <AnimatePresence>
        {isLoading && <LoadingIndicator />}
      </AnimatePresence>
    </div>
  );
};

export default StaffDashboard;